import React, { useEffect } from "react";
import { useImmer } from "use-immer";

import {
  IgButton,
  IgFullWidthGrid,
  IgPaper,
  IgTypography,
} from "@intelligems/ig-theme";
import {
  IntelligemsAppScopeList,
  ProgressBarConfigType,
} from "@intelligems/ig-types";
import { Divider, Grid, Typography } from "@mui/material";

import { useConfiguration } from "../../api/configurations/use-configuration";
import {
  useGetOrg,
  useGetOrgId,
} from "../../api/organizations/get-organization";
import { useIsAdmin } from "../../api/user-auths/is-admin";
import { useRefreshShopifyScopes, useUser } from "../../api/user-auths/login";
import {
  checkAnyScope,
  checkBooleanScope,
} from "../../components/ScopeWrappers/helpers";
import { useHasRequiredShopifyScopes } from "../../components/ScopeWrappers/hooks";
import { SubscriptionPriceSection } from "../../components/SubscriptionPriceSection/SubscriptionPriceSection";
import { IgAlert } from "../../components/Utils/IgAlert";
import IgErrorBoundary from "../../components/Utils/IgErrorBoundary";
import { useViewPage } from "../../hooks/mixpanel";
import { uninstallIntelligems } from "../../utils/linkSheet";
import Loading from "../../utils/Loading";
import openNewTab from "../../utils/openNewTab";

import { AdvancedSettings } from "./AdminSettings/AdvancedSettings";
import { CustomBilling } from "./AdminSettings/CustomBilling";
import { FreeTrialExtender } from "./AdminSettings/FreeTrialExtender";
import { CogsPanel } from "./Cogs/CogsPanel";
import { PricingStatusSettingsSection } from "./PricingStatus/pricingStatusSettingsSection";
import { CssSettings } from "./CssSettings";
import { CurrencySettings } from "./CurrencySettings";
import { OrganizationInformation } from "./OrganizationInformation";
import PersonalInformation from "./PersonalInformation";
import { ScopeSettings } from "./ScopeSettings";
import { ScriptCopy } from "./ScriptCopy";

export const Settings = () => {
  const organization = useGetOrg();
  const organizationId = useGetOrgId();
  const user = useUser();
  const isAdmin = useIsAdmin();
  const refreshShopifyCredentials = useRefreshShopifyScopes();
  const hasRequiredShopifyScopes = useHasRequiredShopifyScopes([
    ...IntelligemsAppScopeList,
  ]);

  const [updatedProgressBarConfig, setUpdatedProgressBarConfig] = useImmer<
    ProgressBarConfigType | undefined
  >(undefined);

  useViewPage("Settings");

  const progressBarConfig = useConfiguration<ProgressBarConfigType>(
    "shipping_progress_bar_config"
  );

  useEffect(() => {
    if (progressBarConfig) {
      setUpdatedProgressBarConfig(progressBarConfig.value);
    }
  }, [progressBarConfig, setUpdatedProgressBarConfig]);

  if (user.isSuccess && progressBarConfig) {
    return (
      <IgErrorBoundary>
        <Grid
          container
          spacing={2}
          direction={"column"}
          alignItems={"flex-start"}
        >
          <IgFullWidthGrid item>
            <Typography variant="h5" sx={{ marginBottom: "5px" }}>
              Settings
            </Typography>
            <Divider />
          </IgFullWidthGrid>
          {hasRequiredShopifyScopes ||
          hasRequiredShopifyScopes === undefined ? null : (
            <IgFullWidthGrid item>
              <IgAlert
                severity={"warning"}
                title={"Update Shopify Permissions"}
                longMessage={
                  <IgTypography>
                    Please reauthorize your store to access latest features.
                  </IgTypography>
                }
                buttons={[
                  <Grid
                    item
                    key={"update-permissions"}
                    sx={{ marginTop: "auto", marginBottom: "auto" }}
                  >
                    <IgButton
                      igVariant={"warning"}
                      loading={refreshShopifyCredentials.isPending}
                      onClick={() => refreshShopifyCredentials.mutate({})}
                    >
                      Update Permissions
                    </IgButton>
                  </Grid>,
                ]}
              />
            </IgFullWidthGrid>
          )}
          <IgFullWidthGrid item>
            <Grid
              container
              direction={"row"}
              alignItems={"stretch"}
              justifyContent={"flex-start"}
            >
              <Grid item xs={6}>
                <OrganizationInformation />
              </Grid>
              <Grid item xs={6}>
                <PersonalInformation />
              </Grid>
            </Grid>
          </IgFullWidthGrid>
          <IgFullWidthGrid item xs>
            <ScriptCopy organizationId={organizationId} />
          </IgFullWidthGrid>
          {isAdmin && organization && organization.data.org && (
            <IgFullWidthGrid item xs>
              <IgPaper>
                <PricingStatusSettingsSection
                  organization={organization.data.org}
                />
              </IgPaper>
            </IgFullWidthGrid>
          )}
          <IgFullWidthGrid item xs>
            <SubscriptionPriceSection
              showHeader={true}
              onSetupLaterClick={() => {}}
            />
          </IgFullWidthGrid>
          <IgFullWidthGrid item xs>
            <CogsPanel />
          </IgFullWidthGrid>
          <IgFullWidthGrid item xs>
            <CssSettings />
          </IgFullWidthGrid>
          <IgFullWidthGrid item xs>
            <CurrencySettings />
          </IgFullWidthGrid>
          <IgFullWidthGrid item xs>
            <IgPaper>
              <Grid item>
                <IgTypography sx={{ fontSize: "15px" }}>
                  Need to uninstall Intelligems? Learn more{" "}
                  <IgTypography
                    sx={{ fontSize: "15px" }}
                    onClick={() => {
                      openNewTab(uninstallIntelligems, "Uninstall Intelligems");
                    }}
                    igStyle={"link"}
                  >
                    here.
                  </IgTypography>
                </IgTypography>
              </Grid>
            </IgPaper>
          </IgFullWidthGrid>
          {isAdmin && organization?.data && (
            <IgFullWidthGrid item xs>
              <IgPaper>
                <FreeTrialExtender organization={organization.data.org} />
              </IgPaper>
            </IgFullWidthGrid>
          )}
          {isAdmin && organization && organization.data && (
            <IgFullWidthGrid item xs>
              <IgPaper>
                <CustomBilling organization={organization.data.org} />
              </IgPaper>
            </IgFullWidthGrid>
          )}
          {checkAnyScope([
            checkBooleanScope(
              "edit_advanced_settings",
              organization.data.org.configurations
            ),
            isAdmin || false,
          ]) && (
            <IgFullWidthGrid item xs>
              <AdvancedSettings />
            </IgFullWidthGrid>
          )}
          {isAdmin && (
            <IgFullWidthGrid item xs>
              <IgPaper>
                <ScopeSettings />
              </IgPaper>
            </IgFullWidthGrid>
          )}
        </Grid>
      </IgErrorBoundary>
    );
  }
  return <Loading />;
};
